import React from 'react';
import {
  Paper, Stack, Button, Divider, TextField, List, ListItemButton, Box, Typography,
  Tooltip, IconButton, Menu, MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EditIcon from '@mui/icons-material/Edit';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import DeleteIcon from '@mui/icons-material/Delete';
import { MenuItem as MuiMenuItem } from '@mui/material';
import type { Conversation } from './teachingChatUtils';

export interface ConversationListProps {
  conversations: Conversation[];
  convQuery: string;
  setConvQuery: (value: string) => void;
  activeConversationId: string | null;
  selectConversation: (id: string | null) => void;
  menuAnchorEl: HTMLElement | null;
  menuConvId: string | null;
  openConvMenu: (evt: React.MouseEvent<HTMLElement>, id: string) => void;
  closeConvMenu: () => void;
  renameConversation: (id: string) => void;
  archiveConversation: (id: string, archived: boolean) => void;
  deleteConversation: (id: string) => void;
}

const ConversationList: React.FC<ConversationListProps> = (p) => (
       <Paper elevation={1} sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', p: 2, height: '100%', minHeight: 0, borderTopLeftRadius: 0, overflow: 'auto' }}>
        <Stack direction="row" justifyContent="flex-end" alignItems="center">
          <Button color="primary" variant="contained" size="small" onClick={() => p.selectConversation(null)} startIcon={<AddIcon />} aria-label="new conversation">
            New chat
          </Button>
        </Stack>
        <Divider sx={{ my: 1 }} />
        <TextField
          size="small"
          fullWidth
          placeholder="Search conversations..."
          sx={{ mb: 1 }}
          value={p.convQuery}
          onChange={(e) => p.setConvQuery(e.target.value.toLowerCase())}
        />
                 <List dense sx={{ flex: 1 }}>
          {p.conversations
            .filter(c => {
              // Hide conversations that are still "Untitled" or start with "Untitled"
              const isUntitled = !c.title || c.title.startsWith('Untitled');
              if (isUntitled) return false; // Never show untitled conversations
              // Show if matches search query (or no search query)
              const matchesSearch = !p.convQuery || (c.title || '').toLowerCase().includes(p.convQuery);
              return matchesSearch;
            })
            .map(c => (
              <ListItemButton 
                key={c._id}
                selected={p.activeConversationId === c._id} 
                onClick={() => p.selectConversation(c._id)}
                sx={{ 
                  py: 0.75,
                  px: 1.5,
                  borderRadius: 1,
                  mb: 0.5,
                  border: 'none',
                  boxShadow: 'none',
                  '&.Mui-selected': {
                    backgroundColor: 'action.selected',
                    boxShadow: 'none',
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: 'action.selected',
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                  <Tooltip title={c.title || 'Untitled'} placement="top" arrow>
                    <Typography variant="body2" noWrap sx={{ flex: 1, fontSize: '0.875rem' }}>
                      {c.title || 'Untitled'}
                    </Typography>
                  </Tooltip>
                  <IconButton size="small" edge="end" aria-label="more" onClick={(e) => p.openConvMenu(e, c._id)} sx={{ opacity: 0.6, transition: 'opacity 120ms', '&:hover': { opacity: 1 } }}>
                    <MoreHorizIcon fontSize="small" />
                  </IconButton>
                </Box>
              </ListItemButton>
          ))}
        </List>
        <Menu anchorEl={p.menuAnchorEl} open={Boolean(p.menuAnchorEl)} onClose={p.closeConvMenu} keepMounted>
          <MuiMenuItem onClick={() => { if (p.menuConvId) p.renameConversation(p.menuConvId); p.closeConvMenu(); }}>
            <EditIcon fontSize="small" style={{ marginRight: 8 }} /> Rename
          </MuiMenuItem>
          <MuiMenuItem onClick={() => { if (p.menuConvId) p.archiveConversation(p.menuConvId, true); p.closeConvMenu(); }}>
            <DriveFileMoveIcon fontSize="small" style={{ marginRight: 8 }} /> Archive
          </MuiMenuItem>
          <MuiMenuItem onClick={() => { if (p.menuConvId) p.deleteConversation(p.menuConvId); p.closeConvMenu(); }}>
            <DeleteIcon fontSize="small" style={{ marginRight: 8 }} /> Delete
          </MuiMenuItem>
        </Menu>
      </Paper>
);

export default ConversationList;
